import { Type } from "@sinclair/typebox";
import type { Static, TSchema } from "@sinclair/typebox";

import {
  ACTION_DETAILS_BATCH_LIMIT,
  ActionGrantSchema,
  ActionDetailsListSchema,
  AdminGuideListSchema,
  ApproveGuideBodySchema,
  GuideLifecycleResultSchema,
  GuideReviewEvidenceSchema,
  GuideReviewSummaryListSchema,
  GuideReviewRetryResultSchema,
  AdminUsageListSchema,
  ModelCacheUsageSnapshotSchema,
  AdminUserListSchema,
  AdminUserSchema,
  AdminUserUsageSchema,
  McpRawUsageSchema,
  AuthOptionsSchema,
  CompleteBootstrapBodySchema,
  HttpPaths,
  GuideListSchema,
  PublicUserSchema,
  RegisterBodySchema,
  RuntimeOptionsSchema,
  RuntimeProfileId,
  SessionListSchema,
  SessionSchema,
  SessionReportSchema,
  SubmissionSchema,
  SessionUsageSummarySchema,
  SubmittedTurnSchema,
  TaskEventListSchema,
  TurnSchema,
  TerminationSchema,
  WorkspaceDirectoryListingSchema,
  WorkspaceFilePreviewSchema,
  WorkspaceInputUploadSchema,
  WorkspaceFileSnapshotSchema,
  FileVersionSchema,
  WorkingPlanSchema,
  parseContract,
} from "@rnavoyager/contracts";
import type {
  ActionDetailsEntryResponse,
  ActionGrantContract,
  ActionRequestContract,
  AdminGuideResponse,
  ApproveGuideBody,
  GuideLifecycleResult,
  GuideReviewEvidenceContract,
  GuideReviewSummary,
  GuideReviewRetryResult,
  AdminUserResponse,
  AdminUserUsageResponse,
  McpRawUsageResponse,
  AuthOptionsResponse,
  CompleteBootstrapBody,
  GuideSummaryResponse,
  PublicUser,
  RegisterBody,
  RuntimeOptionsResponse,
  ResultArtifactsBlock,
  ResultDocument,
  ModelUsageSummaryResponse,
  ModelCacheUsageSnapshot,
  SessionListEntryResponse,
  SessionResponse,
  SessionReportResponse,
  SubmissionResponse,
  CreateSessionBody,
  SessionUsageSummaryResponse,
  SubmittedTurnResponse,
  TaskEventKind,
  TaskEventPayloadSchemas,
  TaskEventResponse,
  TurnResponse,
  Termination,
  ToolUsageSummaryResponse,
  WorkspaceDirectoryListingResponse,
  WorkspaceFilePreviewResponse,
  WorkspaceInputUploadResponse,
  WorkspaceFileSnapshot,
  FileVersion,
  WorkingPlanResponse,
} from "@rnavoyager/contracts";

import type {
  AdminGuidesResponse,
  AuthOptions,
  AuthUser,
  GuideSummary,
  MessageAttachment,
  McpRawUsage,
  ModelUsageSummary,
  PublicPlan,
  RuntimeOptions,
  SessionDetail,
  SessionSummary,
  SessionUsage,
  TaskEvent,
  TaskRuntimeState,
  ToolUsageSummary,
  UsageOverview,
  UserUsage,
  WorkspaceDirectoryListing,
  WorkspaceFilePreview,
  WorkspaceInputUpload,
} from "./types";
import { assertTaskProtocolResponse, taskProtocolSearchParams } from "./protocol-compatibility";
import {
  pendingSubmission,
  rememberSubmission,
  forgetSubmission,
  forgetAllSubmissions,
} from "./submission-recovery";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const jsonHeaders = { "Content-Type": "application/json" };
const sessionUrl = (sessionId: string) => `/api/sessions/${encodeURIComponent(sessionId)}`;
const PROFILE_ID = RuntimeProfileId;
const EVENT_PAGE_SIZE = 100;

const runtimeOptionsCache: { value?: RuntimeOptionsResponse } = {};
const sessionWireCache = new Map<string, SessionResponse>();
const runtimeCache = new Map<string, TaskRuntimeState>();
type WireEvent = {
  [Kind in TaskEventKind]: {
    cursor: string;
    eventId: string;
    sessionId: string;
    turnId?: string;
    generation: number;
    kind: Kind;
    payload: Static<(typeof TaskEventPayloadSchemas)[Kind]>;
    payloadDigest: string;
    occurredAt: string;
  };
}[TaskEventKind];
type AcceptedWireEvent = Extract<WireEvent, { kind: "turn.accepted" }>;
type ActionDisplayDetails = {
  request: ActionRequestContract;
  grant: ActionGrantContract | null;
};

const wireEventsCache = new Map<string, Map<string, WireEvent>>();
const actionDecisionCache = new Map<
  string,
  { decision: ActionApprovalDecision; decidedAt: string }
>();
const guidePreferences = new Map<
  string,
  { preference: "auto" | "none" | "manual"; definitionId: string | null }
>();
const submittedMessages = new Map<string, { content: string; attachments: MessageAttachment[] }>();

export type ActionApprovalDecision = "allow_once" | "allow_session" | "deny";

export async function login(email: string, password: string): Promise<AuthUser> {
  const payload = await requestContract<PublicUser>(PublicUserSchema, "/api/auth/login", {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ email, password }),
  });
  resetSessionViewCaches();
  return authUser(payload);
}

export async function getAuthOptions(): Promise<AuthOptions> {
  const payload = await requestContract<AuthOptionsResponse>(
    AuthOptionsSchema,
    "/api/auth/options",
  );
  return {
    registration_enabled: payload.registrationEnabled,
    password_min_length: payload.passwordMinLength,
    password_max_length: payload.passwordMaxLength,
  };
}

export async function register(input: RegisterBody): Promise<AuthUser> {
  const payload = await requestContract<PublicUser>(PublicUserSchema, HttpPaths.register, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(parseContract(RegisterBodySchema, input)),
  });
  resetSessionViewCaches();
  return authUser(payload);
}

export async function confirmAccountRawArtifactConsent(): Promise<AuthUser> {
  const payload = await requestContract<PublicUser>(
    PublicUserSchema,
    "/api/auth/raw-artifact-consent",
    {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ allowRawArtifacts: true }),
    },
  );
  return authUser(payload);
}

export async function completeAdministratorBootstrap(input: CompleteBootstrapBody): Promise<void> {
  await requestContract<PublicUser>(PublicUserSchema, HttpPaths.completeBootstrap, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(parseContract(CompleteBootstrapBodySchema, input)),
  });
  // 首次改密撤销旧登录状态，页面必须重新登录。
  resetSessionViewCaches();
}

export async function getCurrentUser(): Promise<AuthUser> {
  const user = await requestContract<PublicUser>(PublicUserSchema, "/api/auth/me");
  resetSessionViewCaches();
  return authUser(user);
}

export async function logout(): Promise<void> {
  await requestJson("/api/auth/logout", { method: "POST" });
  resetSessionViewCaches();
  delete runtimeOptionsCache.value;
  forgetAllSubmissions();
}

function resetSessionViewCaches(): void {
  sessionWireCache.clear();
  runtimeCache.clear();
  wireEventsCache.clear();
  actionDecisionCache.clear();
  guidePreferences.clear();
  submittedMessages.clear();
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<AuthUser> {
  const payload = await requestContract<PublicUser>(PublicUserSchema, "/api/auth/password", {
    method: "PATCH",
    headers: jsonHeaders,
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return authUser(payload);
}

export async function getMyUsage(): Promise<UserUsage> {
  const payload = await requestContract<AdminUserUsageResponse>(AdminUserUsageSchema, "/api/usage");
  return userUsage(payload);
}

export async function getSessionUsage(sessionId: string): Promise<SessionUsage> {
  const payload = await requestContract<SessionUsageSummaryResponse>(
    SessionUsageSummarySchema,
    `${sessionUrl(sessionId)}/usage`,
  );
  return sessionUsage(payload);
}

export async function getAdminUsage(): Promise<UsageOverview> {
  const payload = await requestContract<AdminUserUsageResponse[]>(
    AdminUsageListSchema,
    "/api/admin/usage",
  );
  return { generated_at: new Date().toISOString(), users: payload.map(userUsage) };
}

export async function getModelCacheUsage(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<ModelCacheUsageSnapshot> {
  return requestContract<ModelCacheUsageSnapshot>(
    ModelCacheUsageSnapshotSchema,
    `${HttpPaths.adminModelCacheUsage}?${new URLSearchParams({ from, to })}`,
    signal ? { signal } : undefined,
  );
}

export async function getMcpRawUsage(): Promise<McpRawUsage> {
  const payload = await requestContract<McpRawUsageResponse>(
    McpRawUsageSchema,
    "/api/admin/usage/mcp",
  );
  return {
    source: payload.source,
    enabled: payload.enabled,
    measured_at: payload.measuredAt,
    totals: {
      events: payload.totals.events,
      succeeded: payload.totals.succeeded,
      failed: payload.totals.failed,
      wall_time_ms: payload.totals.wallTimeMs,
      input_bytes: payload.totals.inputBytes,
      output_bytes: payload.totals.outputBytes,
      external_calls: payload.totals.externalCalls,
    },
    functions: payload.functions.map((entry) => ({
      service_id: entry.serviceId,
      function_name: entry.functionName,
      invocations: entry.invocations,
      succeeded: entry.succeeded,
      failed: entry.failed,
      wall_time_ms: entry.wallTimeMs,
      external_calls: entry.externalCalls,
    })),
  };
}

export async function listUsers(): Promise<AuthUser[]> {
  const payload = await requestContract<AdminUserResponse[]>(
    AdminUserListSchema,
    "/api/admin/users",
  );
  return payload.map(authUser);
}

export async function activateUser(userId: string): Promise<AuthUser> {
  const payload = await requestContract<AdminUserResponse>(
    AdminUserSchema,
    `/api/admin/users/${encodeURIComponent(userId)}/activate`,
    {
      method: "POST",
    },
  );
  return authUser(payload);
}

export async function changeUserPassword(userId: string, password: string): Promise<AuthUser> {
  const payload = await requestContract<AdminUserResponse>(
    AdminUserSchema,
    `/api/admin/users/${encodeURIComponent(userId)}/password`,
    {
      method: "PATCH",
      headers: jsonHeaders,
      body: JSON.stringify({ password }),
    },
  );
  return authUser(payload);
}

export async function deleteUser(userId: string): Promise<void> {
  await requestJson(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

export async function listAdminGuides(): Promise<AdminGuidesResponse> {
  const rows = await requestContract<AdminGuideResponse[]>(
    AdminGuideListSchema,
    "/api/admin/guides",
  );
  return {
    guides: rows.map((row) => ({
      ...guideSummaryFromDefinition(row.definition),
      declared_status: row.definition.status,
      effective_status: row.effectiveStatus,
      definition: row.definition,
      review: row,
    })),
    management: {
      mode: "repository_skill",
      skill: "author-guided-task",
      web_mutations_enabled: true,
      content_editing: "repository_skill",
      lifecycle_actions: ["approve"],
    },
  };
}

export async function approveAdminGuide(
  definitionId: string,
  body: ApproveGuideBody,
): Promise<GuideLifecycleResult> {
  const request = parseContract(ApproveGuideBodySchema, body);
  return requestContract<GuideLifecycleResult>(
    GuideLifecycleResultSchema,
    HttpPaths.adminGuideApproval.replace(":definitionId", encodeURIComponent(definitionId)),
    { method: "POST", headers: jsonHeaders, body: JSON.stringify(request) },
  );
}

export async function getAdminGuideEvidence(
  definitionId: string,
): Promise<GuideReviewEvidenceContract> {
  return requestContract<GuideReviewEvidenceContract>(
    GuideReviewEvidenceSchema,
    HttpPaths.adminGuideEvidence.replace(":definitionId", encodeURIComponent(definitionId)),
  );
}

export async function getAdminGuideReviews(definitionId: string): Promise<GuideReviewSummary[]> {
  return requestContract<GuideReviewSummary[]>(
    GuideReviewSummaryListSchema,
    HttpPaths.adminGuideReviews.replace(":definitionId", encodeURIComponent(definitionId)),
  );
}

export async function retryAdminGuideReview(
  definitionId: string,
  reviewId: string,
): Promise<GuideReviewRetryResult> {
  return requestContract<GuideReviewRetryResult>(
    GuideReviewRetryResultSchema,
    HttpPaths.adminGuideReviewRetry
      .replace(":definitionId", encodeURIComponent(definitionId))
      .replace(":reviewId", encodeURIComponent(reviewId)),
    { method: "POST", headers: jsonHeaders, body: "{}" },
  );
}

export async function listSessions(): Promise<SessionSummary[]> {
  const rows = await requestContract<SessionListEntryResponse[]>(
    SessionListSchema,
    "/api/sessions",
  );
  for (const row of rows) sessionWireCache.set(row.sessionId, row);
  return rows.map(sessionListSummaryFromWire).sort(compareSessionRecentUserMessage);
}

export async function createSession(seriesId?: string): Promise<SessionDetail> {
  const options = runtimeOptionsCache.value ?? (await loadRuntimeOptionsWire());
  const receipt = await requestContract<SubmissionResponse>(SubmissionSchema, "/api/sessions", {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({
      requestId: newRequestId(),
      seriesId: seriesId ?? options.defaultSeriesId,
      start: { kind: "draft" },
    }),
  });
  const created = receipt.session;
  sessionWireCache.set(created.sessionId, created);
  const runtime = emptyRuntime(created.status);
  runtimeCache.set(created.sessionId, runtime);
  return sessionDetailFromWire(created, runtime);
}

export async function createConversation(
  userId: string,
  goal: string,
  seriesId: string,
  mode: "default" | "plan",
): Promise<SessionDetail> {
  const previous = pendingSubmission(userId);
  if (
    previous &&
    (previous.seriesId !== seriesId ||
      previous.start.kind !== "turn" ||
      previous.start.goal !== goal ||
      previous.start.planMode !== (mode === "plan"))
  )
    throw new ApiError(409, "上一笔提交结果尚未核实，请先恢复该任务。");
  const request: CreateSessionBody = previous ?? {
    requestId: newRequestId(),
    seriesId,
    start: { kind: "turn", goal, planMode: mode === "plan" },
  };
  rememberSubmission(userId, request);
  return completeSubmission(userId, await sendSubmission(request));
}

export async function recoverConversation(userId: string): Promise<SessionDetail | null> {
  const pending = pendingSubmission(userId);
  if (!pending) return null;
  let receipt: SubmissionResponse;
  try {
    receipt = await requestContract<SubmissionResponse>(
      SubmissionSchema,
      `/api/submissions/${encodeURIComponent(pending.requestId)}`,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 410) {
      forgetSubmission(userId);
      return null;
    }
    if (!(error instanceof ApiError) || error.status !== 404) throw error;
    receipt = await sendSubmission(pending);
  }
  return completeSubmission(userId, receipt);
}

function sendSubmission(request: CreateSessionBody): Promise<SubmissionResponse> {
  return requestContract<SubmissionResponse>(SubmissionSchema, "/api/sessions", {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
}

async function completeSubmission(
  userId: string,
  receipt: SubmissionResponse,
): Promise<SessionDetail> {
  sessionWireCache.set(receipt.session.sessionId, receipt.session);
  const session = await getSession(receipt.session.sessionId);
  forgetSubmission(userId);
  return session;
}

export async function waitForSessionReady(sessionId: string): Promise<SessionDetail> {
  const deadline = Date.now() + 180_000;
  for (;;) {
    const session = await getSession(sessionId);
    if (session.status === "active") return session;
    if (session.status !== "provisioning" || Date.now() >= deadline)
      throw new ApiError(409, "工作区尚未就绪，请查看会话准备状态。");
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const session = await requestContract<SessionResponse>(SessionSchema, sessionUrl(sessionId));
  sessionWireCache.set(sessionId, session);
  const runtime = await refreshRuntime(sessionId);
  return sessionDetailFromWire(session, runtime);
}

export async function renameSession(sessionId: string, title: string): Promise<SessionDetail> {
  const session = await requestContract<SessionResponse>(SessionSchema, sessionUrl(sessionId), {
    method: "PATCH",
    headers: jsonHeaders,
    body: JSON.stringify({ title }),
  });
  sessionWireCache.set(sessionId, session);
  return sessionDetailFromWire(session, await refreshRuntime(sessionId));
}

export async function deleteSession(sessionId: string): Promise<void> {
  await requestJson(sessionUrl(sessionId), { method: "DELETE" });
  sessionWireCache.delete(sessionId);
  runtimeCache.delete(sessionId);
  wireEventsCache.delete(sessionId);
  guidePreferences.delete(sessionId);
}

export async function getRuntimeOptions(): Promise<RuntimeOptions> {
  return runtimeOptions(await loadRuntimeOptionsWire());
}

export async function updateSessionSeries(
  sessionId: string,
  seriesId: string,
): Promise<SessionDetail> {
  const updated = await requestContract<SessionResponse>(
    SessionSchema,
    `${sessionUrl(sessionId)}/series`,
    {
      method: "PATCH",
      headers: jsonHeaders,
      body: JSON.stringify({ seriesId }),
    },
  );
  sessionWireCache.set(sessionId, updated);
  return sessionDetailFromWire(updated, await refreshRuntime(sessionId));
}

export async function createTaskTurn(
  sessionId: string,
  content: string,
  collaborationMode: "default" | "plan" = "default",
  attachments: MessageAttachment[] = [],
): Promise<
  TaskRuntimeState & {
    session_title?: string;
    session_title_pending?: boolean;
  }
> {
  return submitTaskTurn(sessionId, content, collaborationMode, attachments, false);
}

async function submitTaskTurn(
  sessionId: string,
  content: string,
  collaborationMode: "default" | "plan",
  attachments: MessageAttachment[],
  forceNewTurn: boolean,
): Promise<
  TaskRuntimeState & {
    session_title?: string;
    session_title_pending?: boolean;
  }
> {
  const requestId = newRequestId();
  submittedMessages.set(requestId, { content, attachments });
  const current = runtimeCache.get(sessionId);
  const active = !forceNewTurn && current ? activeRuntimeState(current.state) : false;
  const guide = guidePreferences.get(sessionId);
  const body = {
    requestId,
    goal: goalWithAttachments(content, attachments),
    imagePaths: attachments
      .filter((item) => /\.(png|jpe?g|webp)$/i.test(item.path))
      .map((item) => item.path),
    planMode: collaborationMode === "plan",
    ...(guide?.preference === "manual"
      ? { guideDefinitionId: guide.definitionId }
      : guide?.preference === "none"
        ? { guideDefinitionId: null }
        : {}),
  };
  const submitted = await requestContract<SubmittedTurnResponse>(
    SubmittedTurnSchema,
    `${sessionUrl(sessionId)}/${active ? "steer" : "turns"}`,
    { method: "POST", headers: jsonHeaders, body: JSON.stringify(body) },
  );
  const cachedSession = sessionWireCache.get(sessionId);
  if (cachedSession) {
    sessionWireCache.set(sessionId, {
      ...cachedSession,
      title: submitted.sessionTitle,
      titlePending: submitted.sessionTitlePending,
      runtimeGeneration: submitted.generation,
    });
  }
  const runtime = await refreshRuntime(sessionId, true);
  return {
    ...runtime,
    session_title: submitted.sessionTitle,
    session_title_pending: submitted.sessionTitlePending,
  };
}

export async function answerTaskTurn(
  sessionId: string,
  _turnId: string,
  _requestId: string,
  answers: Record<string, string>,
): Promise<TaskRuntimeState> {
  const content = Object.entries(answers)
    .map(([question, answer]) => `${question}：${answer}`)
    .join("；");
  const mode = runtimeCache.get(sessionId)?.collaboration_mode ?? "default";
  return submitTaskTurn(sessionId, content, mode, [], true);
}

export async function resumeTask(
  sessionId: string,
  instruction: string | null = null,
): Promise<TaskRuntimeState> {
  return createTaskTurn(sessionId, instruction?.trim() || "请继续上一项任务。");
}

export async function getTaskEvents(sessionId: string, after = 0): Promise<TaskRuntimeState> {
  void after;
  return refreshRuntime(sessionId);
}

export async function getSessionReport(sessionId: string): Promise<SessionReportResponse | null> {
  return requestContract<SessionReportResponse | null>(
    Type.Union([SessionReportSchema, Type.Null()]),
    HttpPaths.sessionReport.replace(":sessionId", encodeURIComponent(sessionId)),
  );
}

export async function retrySessionReport(
  sessionId: string,
  expectedRevision: number,
  evidenceDigest: string,
): Promise<TaskRuntimeState> {
  await requestContract<TurnResponse>(
    TurnSchema,
    HttpPaths.retryReport.replace(":sessionId", encodeURIComponent(sessionId)),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestId: newRequestId(), expectedRevision, evidenceDigest }),
    },
  );
  return refreshRuntime(sessionId);
}

export function taskEventsStreamUrl(sessionId: string, after = 0): string {
  const query = taskProtocolSearchParams(after);
  return `${sessionUrl(sessionId)}/events?${query}`;
}

export async function listGuides(): Promise<GuideSummary[]> {
  const rows = await requestContract<GuideSummaryResponse[]>(GuideListSchema, "/api/guides");
  return rows.map(guideSummary);
}

export async function selectGuide(
  sessionId: string,
  preference: "auto" | "none" | "manual",
  definitionId: string | null = null,
): Promise<TaskRuntimeState> {
  if ((preference === "manual") !== Boolean(definitionId)) {
    throw new ApiError(422, "手动 Guide 选择与 definition ID 不匹配。");
  }
  guidePreferences.set(sessionId, { preference, definitionId });
  return runtimeCache.get(sessionId) ?? refreshRuntime(sessionId);
}

export async function startPlan(
  sessionId: string,
  executionPlanId: string,
  planHash: string,
  revision: number,
): Promise<TaskRuntimeState> {
  void executionPlanId;
  void planHash;
  void revision;
  const turnId = await requireActiveTurn(sessionId);
  await requestContract<TurnResponse>(
    TurnSchema,
    `/api/turns/${encodeURIComponent(turnId)}/start`,
    {
      method: "POST",
    },
  );
  return refreshRuntime(sessionId, true);
}

export async function decideAction(
  sessionId: string,
  actionId: string,
  requestDigest: string,
  decision: ActionApprovalDecision,
): Promise<TaskRuntimeState> {
  const previousDecision = actionDecisionCache.get(actionId);
  actionDecisionCache.set(actionId, {
    decision,
    decidedAt: new Date().toISOString(),
  });
  try {
    if (decision === "deny") return await interruptTask(sessionId);
    const grant = await requestContract<ActionGrantContract>(
      ActionGrantSchema,
      `/api/actions/${encodeURIComponent(actionId)}/approve`,
      {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
          requestDigest,
          scope: decision === "allow_session" ? "session_similar" : "once",
        }),
      },
    );
    actionDecisionCache.set(actionId, { decision, decidedAt: grant.issuedAt });
    return await refreshRuntime(sessionId, true);
  } catch (cause) {
    if (previousDecision) actionDecisionCache.set(actionId, previousDecision);
    else actionDecisionCache.delete(actionId);
    throw cause;
  }
}

export async function discardPlan(
  sessionId: string,
  executionPlanId: string,
  planHash: string,
  revision: number,
): Promise<TaskRuntimeState> {
  void executionPlanId;
  void planHash;
  void revision;
  return interruptTask(sessionId);
}

export async function interruptTask(sessionId: string): Promise<TaskRuntimeState> {
  const previous = runtimeCache.get(sessionId)?.termination;
  if (previous && previous.state !== "completed") {
    await requestContract(
      TerminationSchema,
      `/api/terminations/${encodeURIComponent(previous.terminationId)}/retry`,
      { method: "POST" },
    );
    return refreshRuntime(sessionId, true);
  }
  const turnId = await requireActiveTurn(sessionId);
  const result = await requestContract<TurnResponse>(
    TurnSchema,
    `/api/turns/${encodeURIComponent(turnId)}`,
    {
      method: "DELETE",
    },
  );
  const cached = runtimeCache.get(sessionId);
  if (cached && result.termination)
    runtimeCache.set(sessionId, { ...cached, termination: result.termination, state: "stopping" });
  return refreshRuntime(sessionId, true);
}

export async function listAdminTerminations() {
  return requestContract<Termination[]>(Type.Array(TerminationSchema), "/api/admin/terminations");
}

export async function resolveTermination(
  terminationId: string,
  input: { grantId: string; resourceId: string | null; evidenceDigest: string },
) {
  return requestContract(
    TerminationSchema,
    `/api/terminations/${encodeURIComponent(terminationId)}/resolve`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export async function listWorkspaceFiles(
  sessionId: string,
  path = "/project",
): Promise<WorkspaceDirectoryListing> {
  const query = new URLSearchParams({ path });
  const listing = await requestContract<WorkspaceDirectoryListingResponse>(
    WorkspaceDirectoryListingSchema,
    `${sessionUrl(sessionId)}/workspace/files?${query}`,
  );
  return workspaceDirectoryListing(listing);
}

export async function previewWorkspaceFile(
  sessionId: string,
  path: string,
  signal?: AbortSignal,
): Promise<WorkspaceFilePreview> {
  const query = new URLSearchParams({ path });
  const preview = await requestContract<WorkspaceFilePreviewResponse>(
    WorkspaceFilePreviewSchema,
    `${sessionUrl(sessionId)}/workspace/preview?${query}`,
    { signal },
  );
  return workspaceFilePreview(preview);
}

export async function referenceWorkspaceFile(
  sessionId: string,
  path: string,
  signal?: AbortSignal,
): Promise<FileVersion> {
  const query = new URLSearchParams({ path });
  const snapshot = await requestContract<WorkspaceFileSnapshot>(
    WorkspaceFileSnapshotSchema,
    `${sessionUrl(sessionId)}/workspace/file-snapshot?${query}`,
    { signal },
  );
  if (snapshot.path !== path) throw new Error("文件核验回执与所选路径不一致");
  signal?.throwIfAborted();
  const file = await requestContract<FileVersion>(
    FileVersionSchema,
    `${sessionUrl(sessionId)}/workspace/file-versions`,
    {
      method: "POST",
      signal,
      body: JSON.stringify({ path, expectedDigest: snapshot.digest }),
    },
  );
  if (
    file.sessionId !== sessionId ||
    `/project/${file.path}` !== path ||
    file.digest !== snapshot.digest ||
    file.sizeBytes !== snapshot.sizeBytes
  )
    throw new Error("文件登记回执与会话或内容快照不一致");
  signal?.throwIfAborted();
  return file;
}

export async function uploadWorkspaceInput(
  sessionId: string,
  file: File,
  options: {
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<WorkspaceInputUpload> {
  const query = new URLSearchParams({ filename: file.name });
  const path = `${sessionUrl(sessionId)}/workspace/upload?${query}`;
  if (options.onProgress || options.signal) {
    return uploadWorkspaceInputWithProgress(path, file, options);
  }
  const upload = await requestContract<WorkspaceInputUploadResponse>(
    WorkspaceInputUploadSchema,
    path,
    {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
    },
  );
  return workspaceInputUpload(upload);
}

export async function deleteWorkspaceInput(sessionId: string, path: string): Promise<void> {
  const query = new URLSearchParams({ path });
  await requestJson(`${sessionUrl(sessionId)}/workspace/input?${query}`, { method: "DELETE" });
}

export function workspaceDownloadUrl(sessionId: string, path: string): string {
  const query = new URLSearchParams({ path });
  return `${sessionUrl(sessionId)}/workspace/download?${query}`;
}

export function workspaceContentUrl(sessionId: string, path: string): string {
  const query = new URLSearchParams({ path });
  return `${sessionUrl(sessionId)}/workspace/content?${query}`;
}

async function loadRuntimeOptionsWire(): Promise<RuntimeOptionsResponse> {
  const options = await requestContract<RuntimeOptionsResponse>(
    RuntimeOptionsSchema,
    "/api/runtime/options",
  );
  runtimeOptionsCache.value = options;
  return options;
}

function runtimeOptions(options: RuntimeOptionsResponse): RuntimeOptions {
  return {
    runtime_profile_id: PROFILE_ID,
    series: options.series,
    default_series_id: options.defaultSeriesId,
    workspace_upload: {
      max_file_bytes: options.workspaceUpload.maxFileBytes,
      max_input_files: options.workspaceUpload.maxInputFiles,
      max_input_total_bytes: options.workspaceUpload.maxInputTotalBytes,
    },
  };
}

async function refreshRuntime(sessionId: string, fromBeginning = false): Promise<TaskRuntimeState> {
  const events = await loadWireEvents(sessionId, fromBeginning);
  const runtime = await runtimeFromWireEvents(sessionId, events);
  runtimeCache.set(sessionId, runtime);
  return runtime;
}

async function loadWireEvents(sessionId: string, fromBeginning: boolean): Promise<WireEvent[]> {
  let cache = wireEventsCache.get(sessionId);
  if (!cache || fromBeginning) {
    cache = new Map();
    wireEventsCache.set(sessionId, cache);
  }
  let after =
    cache.size > 0 ? ([...cache.values()].sort(compareWireEvents).at(-1)?.cursor ?? "0") : "0";
  while (true) {
    const query = taskProtocolSearchParams(after);
    const page = (await requestContract<TaskEventResponse[]>(
      TaskEventListSchema,
      `${sessionUrl(sessionId)}/events?${query}`,
      {},
      true,
    )) as WireEvent[];
    for (const event of page) cache.set(event.eventId, event);
    if (page.length < EVENT_PAGE_SIZE) break;
    const next = page.at(-1)?.cursor;
    if (!next || next === after) throw new ApiError(502, "任务事件游标没有前进。");
    after = next;
  }
  return [...cache.values()].sort(compareWireEvents);
}

async function runtimeFromWireEvents(
  sessionId: string,
  wireEvents: WireEvent[],
): Promise<TaskRuntimeState> {
  // 迟到的旧轮次事件不能把当前轮次切回历史任务。
  const latestTurnId =
    [...wireEvents].reverse().find((event) => event.kind === "turn.accepted")?.turnId ?? null;
  const accepted = latestTurnId
    ? wireEvents.find(
        (event): event is AcceptedWireEvent =>
          event.kind === "turn.accepted" && event.turnId === latestTurnId,
      )
    : undefined;
  const plans = await publicPlansByTurn(wireEvents);
  const latestPlan = latestTurnId ? (plans.get(latestTurnId) ?? null) : null;
  const actions = await actionDetails(wireEvents);
  const events = wireEvents.flatMap((event) => {
    const converted = taskEvent(event, plans.get(event.turnId ?? "") ?? null, actions);
    if (
      event.kind !== "action.requested" ||
      event.payload.risk !== "high" ||
      event.payload.approvalReused === true
    ) {
      return [converted];
    }
    const recorded = recordedActionDecision(event, wireEvents, actions.get(event.payload.actionId));
    if (!recorded) return [converted];
    return [
      converted,
      actionDecisionEvent(event, recorded),
      ...(recorded.decision === "deny"
        ? []
        : [actionStartedEvent(event, actions.get(event.payload.actionId), recorded.decidedAt)]),
    ];
  });
  const status = latestTurnId ? turnStatus(latestTurnId, wireEvents) : null;
  const completion = latestTurnId ? latestTurnCompletion(latestTurnId, wireEvents) : null;
  const cachedSession = sessionWireCache.get(sessionId);
  const lifecycle = sessionLifecycleProjection(wireEvents);
  const session = cachedSession ? applySessionLifecycle(cachedSession, lifecycle) : undefined;
  if (session && session !== cachedSession) sessionWireCache.set(sessionId, session);
  const sessionStatus = session?.status ?? lifecycle?.status;
  const termination = [
    ...(runtimeCache.get(sessionId)?.termination
      ? [runtimeCache.get(sessionId)!.termination!]
      : []),
    ...(session?.terminations ?? []),
    // 同一时间戳时，按 cursor 排序的新事件优先于旧缓存。
    ...wireEvents.flatMap((event) =>
      event.kind === "session.termination_updated" ? [event.payload] : [],
    ),
  ]
    .filter(
      (item) =>
        (item.turnId === latestTurnId || item.turnId === null) &&
        (!session || item.targetDigest === session.targetDigest),
    )
    .sort((left, right) => left.updatedAt.localeCompare(right.updatedAt))
    .at(-1);
  const state =
    termination && termination.state !== "completed"
      ? termination.state === "needs_attention"
        ? "stop_failed"
        : "stopping"
      : status === "cancelled" && !termination
        ? "stopping"
        : runtimeState(status, accepted, sessionStatus, completion?.payload.outcome);
  // 旧版界面把结束 Turn 的计划保存在时间线中；runtime.plan 只表示仍可交互的当前计划。
  const plan = activeRuntimeState(state) ? latestPlan : null;
  const summary = lastSummary(wireEvents, latestTurnId);
  const resultDocument = lastResultDocument(wireEvents, latestTurnId);
  const artifacts = [
    ...new Set([...workspaceArtifacts(wireEvents), ...resultDocumentArtifactPaths(resultDocument)]),
  ];
  return {
    runtime_profile_id: PROFILE_ID,
    ...(termination ? { termination } : {}),
    ...(sessionStatus ? { session_status: sessionStatus } : {}),
    state,
    ...(accepted?.payload.purpose === "report" ? { execution_phase: "report" as const } : {}),
    activity: currentActivity(state, latestTurnId, wireEvents),
    active_turn_id: activeRuntimeState(state) ? latestTurnId : null,
    collaboration_mode:
      accepted?.kind === "turn.accepted" && accepted.payload.planMode ? "plan" : "default",
    pending_goal: accepted?.kind === "turn.accepted" ? visibleGoal(accepted.payload.goal) : null,
    pending_request_id: state === "awaiting_user_input" ? (completion?.eventId ?? null) : null,
    pending_kind: state === "awaiting_user_input" ? "user_input" : null,
    events,
    events_cursor: events.at(-1)?.cursor ?? 0,
    next_cursor: events.at(-1)?.cursor ?? 0,
    guide_preference: guidePreferences.get(sessionId)?.preference ?? "auto",
    manual_guide_id: guidePreferences.get(sessionId)?.definitionId ?? null,
    guide_match:
      accepted?.kind === "turn.accepted" && accepted.payload.guide
        ? {
            definition_id: accepted.payload.guide.definitionId,
            display_name: accepted.payload.guide.title,
            description: accepted.payload.guide.matchReason,
            content_sha256: accepted.payload.guide.contentDigest,
            reason: accepted.payload.guide.matchReason,
          }
        : null,
    plan,
    plan_hash: plan?.plan_hash ?? null,
    revision: plan?.revision ?? null,
    summary,
    result_document: resultDocument,
    artifact_ids: resultDocumentArtifactIds(resultDocument),
    artifacts,
  };
}

async function publicPlansByTurn(events: WireEvent[]): Promise<Map<string, PublicPlan>> {
  const acceptedByTurn = new Map<string, AcceptedWireEvent>();
  const plannedTurnIds = new Set<string>();
  for (const event of events) {
    if (event.kind === "turn.accepted" && event.turnId) {
      acceptedByTurn.set(event.turnId, event);
    } else if (event.kind === "plan.updated" && event.turnId) {
      plannedTurnIds.add(event.turnId);
    }
  }
  const pairs = await Promise.all(
    [...plannedTurnIds].map(async (turnId) => {
      const plan = await currentPublicPlan(turnId, acceptedByTurn.get(turnId), events);
      return plan ? ([turnId, plan] as const) : null;
    }),
  );
  return new Map(pairs.filter((pair): pair is readonly [string, PublicPlan] => pair !== null));
}

async function currentPublicPlan(
  turnId: string,
  accepted: AcceptedWireEvent | undefined,
  events: WireEvent[],
): Promise<PublicPlan | null> {
  const hasPlan = events.some((event) => event.kind === "plan.updated" && event.turnId === turnId);
  if (!hasPlan) return null;
  let plan: WorkingPlanResponse;
  try {
    plan = await requestContract<WorkingPlanResponse>(
      WorkingPlanSchema,
      `/api/turns/${encodeURIComponent(turnId)}/plan`,
    );
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return null;
    throw cause;
  }
  const goal = accepted?.kind === "turn.accepted" ? visibleGoal(accepted.payload.goal) : "任务计划";
  return {
    runtime_profile_id: PROFILE_ID,
    execution_plan_id: plan.planId,
    plan_id: plan.planId,
    revision: plan.revision,
    plan_hash: plan.digest,
    objective: goal,
    user_goal: goal,
    preview: plan.steps.map(({ title }) => title).join(" → "),
    operations: plan.steps.map((step) => ({
      operation_id: step.stepId,
      description: step.title,
      status: step.status,
      ...(step.acceptanceCriteria ? { acceptance_criteria: step.acceptanceCriteria } : {}),
      kind: "decision",
      tool: null,
      parameters: {},
    })),
    estimated_cost: {},
    data_disclosure: {},
    guide:
      accepted?.kind === "turn.accepted" && accepted.payload.guide
        ? {
            definition_id: accepted.payload.guide.definitionId,
            display_name: accepted.payload.guide.title,
            description: accepted.payload.guide.matchReason,
            content_sha256: accepted.payload.guide.contentDigest,
          }
        : null,
    assumptions: [],
    human_checkpoints: [],
    risk_flags: [],
    repair_policy: "由当前 WorkingPlan 修订控制",
    loop_policy: "每个动作完成后重新决策",
    rationale: "",
  };
}

/** 历史版本按需读取，不用当前规划补齐过去的步骤。 */
export async function getTaskPlanRevision(
  turnId: string,
  revision: number,
): Promise<WorkingPlanResponse> {
  const plan = await requestContract<WorkingPlanResponse>(
    WorkingPlanSchema,
    `/api/turns/${encodeURIComponent(turnId)}/plan?revision=${revision}`,
  );
  if (plan.turnId !== turnId || plan.revision !== revision) {
    throw new ApiError(502, "任务规划版本绑定不一致");
  }
  return plan;
}

async function actionDetails(events: WireEvent[]): Promise<Map<string, ActionDisplayDetails>> {
  const ids = [
    ...new Set(
      events.flatMap((event) =>
        event.kind === "action.requested" ? [event.payload.actionId] : [],
      ),
    ),
  ];
  const entries: ActionDetailsEntryResponse[] = [];
  for (let index = 0; index < ids.length; index += ACTION_DETAILS_BATCH_LIMIT) {
    try {
      entries.push(
        ...(await loadActionDetails(ids.slice(index, index + ACTION_DETAILS_BATCH_LIMIT))),
      );
    } catch {
      // 动作详情只是展示增强：读取失败时保留事件本身，不阻断整段时间线。
    }
  }
  return new Map(
    entries.map(
      (entry) => [entry.actionId, { request: entry.request, grant: entry.grant }] as const,
    ),
  );
}

function loadActionDetails(actionIds: readonly string[]): Promise<ActionDetailsEntryResponse[]> {
  const query = new URLSearchParams({ ids: actionIds.join(",") });
  return requestContract<ActionDetailsEntryResponse[]>(
    ActionDetailsListSchema,
    `/api/actions?${query}`,
  );
}

function taskEvent(
  event: WireEvent,
  plan: PublicPlan | null,
  actions: Map<string, ActionDisplayDetails>,
): TaskEvent {
  const base = {
    ...("turnId" in event ? { turn_id: event.turnId } : {}),
    event_id: event.eventId,
    cursor: cursorNumber(event.cursor),
    timestamp: event.occurredAt,
  };
  if (event.kind === "turn.accepted") {
    if (event.payload.purpose === "report")
      return {
        ...base,
        event_type: "report_delivery_started",
        role: "system",
        message: "研究已完成，正在生成最终报告。",
      };
    const submitted = submittedMessages.get(event.payload.requestId);
    return {
      ...base,
      event_type: "user_message",
      role: "user",
      content: submitted?.content ?? visibleGoal(event.payload.goal),
      payload: {
        request_id: event.payload.requestId,
        attachments: submitted?.attachments ?? attachmentsFromGoal(event.payload.goal),
      },
    };
  }
  if (event.kind === "turn.transitioned") {
    if (event.payload.to === "cancelled") {
      return {
        ...base,
        event_type: "task_interrupted",
        message: "已请求停止任务，正在确认工具执行已终止。",
        payload: { reason: event.payload.reason },
      };
    }
    if (event.payload.from === "waiting_plan" && event.payload.reason === "plan_confirmed") {
      return {
        ...base,
        event_type: "plan_execution_started",
        message: "计划已确认，开始执行。",
      };
    }
    return {
      ...base,
      event_type: "turn_transitioned",
      payload: {
        from: event.payload.from,
        to: event.payload.to,
        reason: event.payload.reason,
      },
    };
  }
  if (event.kind === "session.termination_updated") {
    return {
      ...base,
      event_type: "task_status",
      message:
        event.payload.state === "completed"
          ? "工具执行已确认终止。"
          : event.payload.state === "needs_attention"
            ? "远端提交结果未知，需要管理员核实。"
            : "正在停止并核实工具执行状态。",
      payload: event.payload,
    };
  }
  if (event.kind === "plan.updated") {
    return {
      ...base,
      event_type: plan?.revision === 1 ? "plan_ready" : "working_plan_updated",
      plan_revision: event.payload,
      payload: { plan },
    };
  }
  if (event.kind === "model.activity") {
    const role = { planner: "规划与审核", worker: "步骤处理", vision: "图像理解" }[
      event.payload.role
    ];
    const stage = {
      started: "开始",
      completed: "完成",
      failed: "失败",
      review: "审核",
      vision_fallback: "切换视觉候选",
      paused: "暂停",
    }[event.payload.stage];
    return {
      ...base,
      event_type: "model_activity",
      message: `${role} · ${event.payload.modelDisplayName} · ${stage}${event.payload.reason ? `：${event.payload.reason}` : ""}`,
      payload: { ...event.payload },
    };
  }
  if (event.kind === "model.decision") {
    return {
      ...base,
      event_type: "planning_progress",
      message: decisionProgress(event.payload.decisionKind),
      payload: {
        stage: event.payload.decisionKind,
        input_tokens: event.payload.inputTokens,
        output_tokens: event.payload.outputTokens,
      },
    };
  }
  if (event.kind === "model.action_rejected") {
    const progress = actionRejectionProgress(event.payload.reason);
    return {
      ...base,
      event_type: "planning_progress",
      message: progress.message,
      ...(event.payload.fileInputs?.length ? { file_input_rejection: event.payload } : {}),
      payload: { stage: progress.stage, reason: event.payload.reason },
    };
  }
  if (event.kind === "file.preparation_completed") {
    return { ...base, event_type: "file_preparation", file_preparation: event.payload };
  }
  if (event.kind === "action.requested") {
    const details = actions.get(event.payload.actionId);
    const common = actionActivityPayload(event, details);
    if (event.payload.risk === "high" && event.payload.approvalReused !== true) {
      return {
        ...base,
        event_type: "action_approval_requested",
        file_action: fileActionProjection(event, details, "waiting_approval"),
        payload: {
          ...common,
          public_summary: `调用 ${event.payload.functionName}`,
          reason: "该动作具有外部副作用或破坏性，必须由你明确确认。",
          requested_charge: details?.request.budget ?? {},
        },
      };
    }
    return {
      ...base,
      timestamp: details?.grant?.issuedAt ?? base.timestamp,
      event_type: "action_started",
      file_action: fileActionProjection(event, details, "requested"),
      payload: common,
    };
  }
  if (event.kind === "action.progress") {
    const tool = toolDescriptor(event.payload.serverId, event.payload.functionName);
    return {
      ...base,
      event_type: "action_progress",
      payload: {
        action_id: event.payload.actionId,
        tool,
        job_progress: { ...event.payload.progress, tool: tool.tool, function: tool.function },
      },
    };
  }
  if (event.kind === "action.completed") {
    const details = actions.get(event.payload.actionId);
    const action = details?.request;
    const result = event.payload.result;
    const resultRecord =
      result && typeof result === "object" && !Array.isArray(result)
        ? (result as Record<string, unknown>)
        : {};
    const tool = toolDescriptor(action?.serverId, action?.functionName);
    const startedAt = details?.grant?.issuedAt ?? action?.createdAt ?? event.occurredAt;
    const durationSeconds = elapsedSeconds(startedAt, event.occurredAt);
    const jobProgress = jobProgressProjection(resultRecord, tool);
    const actionFailure = actionFailureProjection(event.payload.status, resultRecord);
    return {
      ...base,
      event_type: "action_completed",
      file_action: fileActionProjection(event, details, event.payload.status),
      message: typeof resultRecord.message === "string" ? resultRecord.message : undefined,
      payload: {
        action_id: event.payload.actionId,
        status: event.payload.status,
        tool,
        started_at: startedAt,
        finished_at: event.occurredAt,
        duration_seconds: durationSeconds,
        ...(jobProgress ? { job_progress: jobProgress } : {}),
        ...(actionFailure ? { action_failure: actionFailure } : {}),
        public_result: resultRecord,
        actual_cost: event.payload.actualCost,
      },
    };
  }
  if (event.kind === "result.updated") {
    return {
      ...base,
      event_type: "result_document_updated",
      result_document: event.payload.resultDocument as ResultDocument,
      payload: {
        revision: event.payload.resultDocument.revision,
        result_document: event.payload.resultDocument as ResultDocument,
      },
    };
  }
  if (event.kind === "turn.completed") {
    const summary = responseText(event.payload.response);
    if (event.payload.outcome === "paused") {
      const questions = responseQuestions(event.payload.response, summary);
      return {
        ...base,
        event_type: "request_user_input",
        message: summary,
        payload: {
          request_id: event.eventId,
          questions,
        },
      };
    }
    return {
      ...base,
      event_type: "agentic_loop_completed",
      content: summary,
      ...(event.payload.resultDocument
        ? { result_document: event.payload.resultDocument as ResultDocument }
        : {}),
      payload: {
        summary,
        outcome: event.payload.outcome,
        ...(event.payload.resultDocument
          ? { result_document: event.payload.resultDocument as ResultDocument }
          : {}),
      },
    };
  }
  if (event.kind === "turn.failed") {
    return {
      ...base,
      event_type:
        event.payload.code === "application_restarted" ? "task_interrupted" : "task_failed",
      message: event.payload.message,
      payload: {
        failure_code: event.payload.code,
        failure_stage: event.payload.stage,
      },
    };
  }
  return {
    ...base,
    event_type: event.kind.replaceAll(".", "_"),
    payload: event.payload as Record<string, unknown>,
  };
}

function responseQuestions(
  response: unknown,
  fallback: string,
): Array<{
  id: string;
  question: string;
  options?: Array<{ label: string; description?: string }>;
}> {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return [{ id: "answer", question: fallback }];
  }
  const values = (response as Record<string, unknown>).questions;
  if (!Array.isArray(values)) return [{ id: "answer", question: fallback }];
  const questions = values.slice(0, 3).flatMap((value, index) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return [];
    const item = value as Record<string, unknown>;
    if (typeof item.question !== "string" || !item.question.trim()) return [];
    const options = Array.isArray(item.options)
      ? item.options.slice(0, 3).flatMap((option) => {
          if (!option || typeof option !== "object" || Array.isArray(option)) return [];
          const candidate = option as Record<string, unknown>;
          if (typeof candidate.label !== "string" || !candidate.label.trim()) return [];
          return [
            {
              label: candidate.label.trim(),
              ...(typeof candidate.description === "string"
                ? { description: candidate.description.trim() }
                : {}),
            },
          ];
        })
      : [];
    return [
      {
        id: typeof item.id === "string" && item.id.trim() ? item.id : `answer_${index + 1}`,
        question: item.question.trim(),
        ...(options.length > 0 ? { options } : {}),
      },
    ];
  });
  return questions.length > 0 ? questions : [{ id: "answer", question: fallback }];
}

function recordedActionDecision(
  event: Extract<WireEvent, { kind: "action.requested" }>,
  events: WireEvent[],
  details: ActionDisplayDetails | undefined,
): { decision: ActionApprovalDecision; decidedAt: string } | null {
  const cached = actionDecisionCache.get(event.payload.actionId);
  if (cached) return cached;
  if (details?.grant) {
    return { decision: "allow_once", decidedAt: details.grant.issuedAt };
  }
  const completed = events.find(
    (candidate) =>
      candidate.kind === "action.completed" &&
      candidate.payload.actionId === event.payload.actionId,
  );
  return completed
    ? {
        decision: "allow_once",
        decidedAt: details?.grant?.issuedAt ?? completed.occurredAt,
      }
    : null;
}

function actionStartedEvent(
  event: Extract<WireEvent, { kind: "action.requested" }>,
  details: ActionDisplayDetails | undefined,
  startedAt: string,
): TaskEvent {
  return {
    event_id: `${event.eventId}:started`,
    turn_id: event.turnId,
    cursor: cursorNumber(event.cursor),
    timestamp: details?.grant?.issuedAt ?? startedAt,
    event_type: "action_started",
    file_action: fileActionProjection(event, details, "requested"),
    payload: actionActivityPayload(event, details),
  };
}

function fileActionProjection(
  event: Extract<WireEvent, { kind: "action.requested" | "action.completed" }>,
  details: ActionDisplayDetails | undefined,
  status: NonNullable<TaskEvent["file_action"]>["status"],
): TaskEvent["file_action"] {
  const action = details?.request;
  const manifest = action?.inputManifest;
  if (
    !action ||
    action.actionId !== event.payload.actionId ||
    action.sessionId !== event.sessionId ||
    !manifest?.files.length ||
    manifest.identityId !== action.identityId ||
    manifest.sessionId !== action.sessionId ||
    manifest.generation !== action.generation ||
    manifest.targetDigest !== action.targetDigest ||
    manifest.functionSchemaDigest !== action.functionSchemaDigest ||
    manifest.argumentsDigest !== action.argumentsDigest ||
    (event.kind === "action.requested" && action.requestDigest !== event.payload.requestDigest)
  )
    return undefined;
  return {
    actionId: action.actionId,
    functionName: action.functionName,
    status,
    inputs: manifest.files,
  };
}

function actionActivityPayload(
  event: Extract<WireEvent, { kind: "action.requested" }>,
  details: ActionDisplayDetails | undefined,
) {
  const action = details?.request;
  return {
    action_id: event.payload.actionId,
    request_digest: event.payload.requestDigest,
    kind: "tool",
    tool: toolDescriptor(action?.serverId, action?.functionName ?? event.payload.functionName),
    public_details: {
      function: event.payload.functionName,
      ...(action ? { server: action.serverId, arguments: action.arguments } : {}),
    },
  };
}

function toolDescriptor(serverId: string | undefined, functionName: string | undefined) {
  const server = serverId ?? "bioscience-tools-mcp";
  const name = functionName ?? "受控动作";
  // MCP 函数名按线协议原样透传；tool 只用于按工具隔离远端作业，不参与展示改写。
  return { server, tool: mcpToolNamespace(name) ?? server, function: name };
}

function mcpToolNamespace(functionName: string): string | null {
  const separator = functionName.indexOf(".");
  return separator > 0 && separator < functionName.length - 1
    ? functionName.slice(0, separator)
    : null;
}

function jobProgressProjection(
  result: Record<string, unknown>,
  tool: { server: string; tool: string; function: string },
): Record<string, unknown> | null {
  const nestedProgress =
    result.job_progress &&
    typeof result.job_progress === "object" &&
    !Array.isArray(result.job_progress)
      ? (result.job_progress as Record<string, unknown>)
      : null;
  // 历史事件已使用 job_progress 命名空间，实时工具结果仍返回顶层字段；
  // 两种来源都必须经过同一白名单投影，不能直接透传历史或工具 payload。
  const source = nestedProgress ?? result;
  const jobId = projectionString(source.job_id, 256);
  const status = projectionString(source.status, 128);
  if (!jobId || !status) return null;
  const queue = projectionQueue(source.queue);
  const elapsed = projectionNonNegativeNumber(source.elapsed_seconds);
  const pollCount = projectionNonNegativeInteger(source.poll_count);
  return {
    job_id: jobId,
    tool: tool.tool,
    function: tool.function,
    status,
    ...(typeof source.terminal === "boolean" ? { terminal: source.terminal } : {}),
    ...(typeof source.result_available === "boolean"
      ? { result_available: source.result_available }
      : {}),
    ...(typeof source.wait_expired === "boolean" ? { wait_expired: source.wait_expired } : {}),
    ...(elapsed !== null ? { elapsed_seconds: elapsed } : {}),
    ...(pollCount !== null ? { poll_count: pollCount } : {}),
    ...(queue ? { queue } : {}),
  };
}

function projectionQueue(value: unknown): Record<string, number> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  const queue: Record<string, number> = {};
  for (const key of ["position", "jobs_ahead", "running_jobs", "total_queued"] as const) {
    const number = projectionNonNegativeInteger(source[key]);
    if (number !== null) queue[key] = number;
  }
  return Object.keys(queue).length > 0 ? queue : null;
}

function actionFailureProjection(
  status: "succeeded" | "failed" | "cancelled" | "timed_out",
  result: Record<string, unknown>,
): Record<string, unknown> | null {
  if (status === "succeeded") return null;
  const message =
    projectionString(result.message, 2_000) ??
    projectionString(result.error, 2_000) ??
    (status === "timed_out"
      ? "动作执行超时"
      : status === "cancelled"
        ? "动作已取消"
        : "动作执行失败");
  const code = projectionString(result.code, 128);
  return { message, ...(code ? { code } : {}) };
}

function projectionString(value: unknown, maximumLength: number): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized ? normalized.slice(0, maximumLength) : null;
}

function projectionNonNegativeNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function projectionNonNegativeInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function elapsedSeconds(startedAt: string, finishedAt: string): number {
  const started = Date.parse(startedAt);
  const finished = Date.parse(finishedAt);
  if (!Number.isFinite(started) || !Number.isFinite(finished) || finished < started) return 0;
  return (finished - started) / 1_000;
}

function actionDecisionEvent(
  event: Extract<WireEvent, { kind: "action.requested" }>,
  recorded: { decision: ActionApprovalDecision; decidedAt: string },
): TaskEvent {
  return {
    event_id: `${event.eventId}:decision`,
    cursor: cursorNumber(event.cursor),
    timestamp: recorded.decidedAt,
    event_type: recorded.decision === "deny" ? "action_denied" : "action_approved",
    message:
      recorded.decision === "deny" ? "已拒绝该动作并停止任务。" : "已允许该动作，继续执行任务。",
    payload: {
      action_id: event.payload.actionId,
      request_digest: event.payload.requestDigest,
      decision: recorded.decision,
    },
  };
}

function turnStatus(turnId: string, events: WireEvent[]): TurnResponse["status"] | null {
  let status: TurnResponse["status"] | null = null;
  for (const event of events) {
    if (event.turnId !== turnId) continue;
    if (event.kind === "turn.accepted") status = "accepted";
    else if (event.kind === "turn.transitioned") status = event.payload.to;
    else if (
      event.kind === "action.requested" &&
      event.payload.risk === "high" &&
      event.payload.approvalReused !== true
    ) {
      // 后端以 action.requested 与数据库 waiting_approval 状态同事务提交；该事件本身就是
      // SSE 状态投影的事实，不能要求额外的 turn.transitioned 才展示人工审批。
      status = "waiting_approval";
    } else if (event.kind === "turn.completed") status = "completed";
    else if (event.kind === "turn.failed") status = "failed";
  }
  return status;
}

function runtimeState(
  status: TurnResponse["status"] | null,
  accepted: AcceptedWireEvent | undefined,
  sessionStatus: SessionResponse["status"] | undefined,
  completionOutcome: "completed" | "reply" | "paused" | undefined,
): TaskRuntimeState["state"] {
  if (sessionStatus === "failed") return "failed";
  if (sessionStatus === "deleting" || sessionStatus === "deleted") return "cancelled";
  if (sessionStatus === "provisioning" || sessionStatus === "rotating") return "preparing";
  if (!status) return "idle";
  if (status === "accepted") {
    return accepted?.kind === "turn.accepted" && accepted.payload.planMode
      ? "planning"
      : "executing";
  }
  if (status === "running") return "running";
  if (status === "waiting_plan") return "awaiting_plan_start";
  if (status === "waiting_approval") return "awaiting_action_approval";
  if (status === "completed" && completionOutcome === "paused") {
    return "awaiting_user_input";
  }
  return status;
}

function latestTurnCompletion(
  turnId: string,
  events: WireEvent[],
): Extract<WireEvent, { kind: "turn.completed" }> | null {
  return (
    [...events]
      .reverse()
      .find(
        (event): event is Extract<WireEvent, { kind: "turn.completed" }> =>
          event.kind === "turn.completed" && event.turnId === turnId,
      ) ?? null
  );
}

interface SessionLifecycleProjection {
  readonly status: SessionResponse["status"];
  readonly runtimeGeneration: number;
  readonly targetDigest?: string;
}

function sessionLifecycleProjection(events: WireEvent[]): SessionLifecycleProjection | undefined {
  let projection: SessionLifecycleProjection | undefined;
  for (const event of events) {
    if (event.kind === "session.provisioning") {
      projection = { status: "provisioning", runtimeGeneration: event.generation };
    } else if (event.kind === "session.rotating") {
      projection = { status: "rotating", runtimeGeneration: event.generation };
    } else if (event.kind === "session.generation_provisioning") {
      projection = { status: "provisioning", runtimeGeneration: event.generation };
    } else if (event.kind === "session.active") {
      projection = {
        status: "active",
        runtimeGeneration: event.generation,
        targetDigest: event.payload.targetDigest,
      };
    } else if (event.kind === "session.failed") {
      projection = { status: "failed", runtimeGeneration: event.generation };
    } else if (event.kind === "session.deleting") {
      projection = { status: "deleting", runtimeGeneration: event.generation };
    }
  }
  return projection;
}

function applySessionLifecycle(
  session: SessionResponse,
  projection: SessionLifecycleProjection | undefined,
): SessionResponse {
  if (!projection || session.status === "deleted") return session;
  if (projection.runtimeGeneration < session.runtimeGeneration) return session;
  const targetDigest = projection.targetDigest ?? session.targetDigest;
  if (
    projection.status === session.status &&
    projection.runtimeGeneration === session.runtimeGeneration &&
    targetDigest === session.targetDigest
  ) {
    return session;
  }
  return {
    ...session,
    status: projection.status,
    runtimeGeneration: projection.runtimeGeneration,
    targetDigest,
  };
}

/**
 * 列表条目的运行时状态由服务端投影提供，客户端不再为每个会话重放事件历史。
 */
function sessionListSummaryFromWire(session: SessionListEntryResponse): SessionSummary {
  return {
    id: session.sessionId,
    title: session.title,
    task_title_assigned: !session.titlePending,
    status: session.status,
    run_status: sessionRunStatus(session.runtimeState),
    runtime_state: session.runtimeState,
    active_run_id: session.activeTurnId,
    created_at: session.createdAt,
    last_activity_at: session.lastActivityAt,
    expires_at: session.createdAt,
    state_version: session.runtimeGeneration,
    runtime_profile_id: PROFILE_ID,
    series_id: session.seriesId,
  };
}

function latestUserMessageAt(runtime: TaskRuntimeState | undefined, fallback: string): string {
  let latest = fallback;
  let latestTime = timestampValue(fallback);
  for (const event of runtime?.events ?? []) {
    if (event.event_type !== "user_message") continue;
    const eventTime = timestampValue(event.timestamp);
    if (eventTime > latestTime || !Number.isFinite(latestTime)) {
      latest = event.timestamp;
      latestTime = eventTime;
    }
  }
  return latest;
}

function compareSessionRecentUserMessage(left: SessionSummary, right: SessionSummary): number {
  return (
    timestampValue(right.last_activity_at) - timestampValue(left.last_activity_at) ||
    timestampValue(right.created_at) - timestampValue(left.created_at) ||
    left.id.localeCompare(right.id)
  );
}

function timestampValue(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function sessionDetailFromWire(session: SessionResponse, runtime: TaskRuntimeState): SessionDetail {
  const sessionStatus = runtime.session_status ?? session.status;
  return {
    id: session.sessionId,
    title: session.title,
    task_title_assigned: !session.titlePending,
    status: sessionStatus,
    run_status: sessionRunStatus(runtime.state),
    runtime_state: runtime.state,
    active_run_id: runtime.active_turn_id ?? null,
    created_at: session.createdAt,
    last_activity_at: latestUserMessageAt(runtime, session.createdAt),
    expires_at: session.createdAt,
    state_version: session.runtimeGeneration,
    runtime_profile_id: PROFILE_ID,
    series_id: session.seriesId,
    state: {},
    runtime,
    provider_consent: {
      provider_id: session.seriesId ?? "unavailable",
      model_name: session.seriesDisplayName,
      provider_config_digest: session.seriesConfigDigest,
      allow_raw_artifacts: true,
      confirmed_at: session.createdAt,
    },
    instance_name: "",
    image_digest: session.targetDigest,
  };
}

function emptyRuntime(sessionStatus: SessionResponse["status"]): TaskRuntimeState {
  return {
    runtime_profile_id: PROFILE_ID,
    session_status: sessionStatus,
    state: runtimeState(null, undefined, sessionStatus, undefined),
    active_turn_id: null,
    collaboration_mode: "default",
    events: [],
    events_cursor: 0,
    next_cursor: 0,
    guide_preference: "auto",
    manual_guide_id: null,
    guide_match: null,
    plan: null,
    plan_hash: null,
    revision: null,
    summary: null,
    artifact_ids: [],
    artifacts: [],
  };
}

function authUser(user: PublicUser | AdminUserResponse): AuthUser {
  return {
    id: user.userId,
    email: user.email,
    name: user.name,
    institution: user.institution,
    gender: user.gender,
    role: user.role,
    must_rotate_credentials: user.mustRotateCredentials,
    allow_raw_artifacts: user.allowRawArtifacts,
    status: user.status,
    created_at: user.createdAt,
  };
}

function userUsage(usage: AdminUserUsageResponse): UserUsage {
  return {
    user_id: usage.userId,
    email: usage.email,
    total_model_calls: usage.totalModelCalls,
    total_input_tokens: usage.totalInputTokens,
    total_output_tokens: usage.totalOutputTokens,
    total_tokens: usage.totalTokens,
    unreported_model_calls: usage.unreportedModelCalls,
    total_tool_calls: usage.totalToolCalls,
    models: usage.models.map(modelUsageSummary),
    tools: usage.tools.map(toolUsageSummary),
  };
}

function modelUsageSummary(model: ModelUsageSummaryResponse): ModelUsageSummary {
  const binding = runtimeModelBindingByDigest(model.modelId);
  return {
    provider_id: binding?.providerId ?? "unavailable",
    model_id: binding?.modelId ?? model.modelId,
    model_display_name: model.modelDisplayName,
    calls: model.calls,
    input_tokens: model.inputTokens,
    output_tokens: model.outputTokens,
    total_tokens: model.totalTokens,
    unreported_calls: model.unreportedCalls,
    first_used_at: model.firstUsedAt,
    last_used_at: model.lastUsedAt,
  };
}

function toolUsageSummary(tool: ToolUsageSummaryResponse): ToolUsageSummary {
  return {
    server_id: tool.serverId,
    tool_name: tool.serverId,
    function_name: tool.functionName,
    calls: tool.calls,
    succeeded_calls: tool.succeededCalls,
    failed_calls: tool.failedCalls,
    first_used_at: tool.firstUsedAt,
    last_used_at: tool.lastUsedAt,
  };
}

function sessionUsage(usage: SessionUsageSummaryResponse): SessionUsage {
  return {
    session_id: usage.sessionId,
    totals: {
      model_calls: usage.totals.modelCalls,
      input_tokens: usage.totals.inputTokens,
      output_tokens: usage.totals.outputTokens,
      total_tokens: usage.totals.totalTokens,
      unreported_model_calls: usage.totals.unreportedModelCalls,
      tool_calls: usage.totals.toolCalls,
      succeeded_tool_calls: usage.totals.succeededToolCalls,
      failed_tool_calls: usage.totals.failedToolCalls,
      provider_search_executions: usage.totals.providerSearchExecutions,
      provider_search_calls: usage.totals.providerSearchCalls,
      provider_search_input_tokens: usage.totals.providerSearchInputTokens,
      provider_search_output_tokens: usage.totals.providerSearchOutputTokens,
      provider_search_unreported_calls: usage.totals.providerSearchUnreportedCalls,
    },
    models: usage.models.map(modelUsageSummary),
    tools: usage.tools.map(toolUsageSummary),
    provider_search: {
      executions: usage.providerSearch.executions,
      search_calls: usage.providerSearch.searchCalls,
      input_tokens: usage.providerSearch.inputTokens,
      output_tokens: usage.providerSearch.outputTokens,
      total_tokens: usage.providerSearch.totalTokens,
      unreported_calls: usage.providerSearch.unreportedCalls,
    },
    turns: usage.turns.map((turn) => ({
      turn_id: turn.turnId,
      status: turn.status,
      created_at: turn.createdAt,
      model_calls: turn.modelCalls,
      input_tokens: turn.inputTokens,
      output_tokens: turn.outputTokens,
      total_tokens: turn.totalTokens,
      unreported_model_calls: turn.unreportedModelCalls,
      tool_calls: turn.toolCalls,
      succeeded_tool_calls: turn.succeededToolCalls,
      failed_tool_calls: turn.failedToolCalls,
      provider_search_executions: turn.providerSearchExecutions,
      provider_search_calls: turn.providerSearchCalls,
    })),
  };
}

function runtimeModelBindingByDigest(modelConfigDigest: string) {
  for (const series of runtimeOptionsCache.value?.series ?? []) {
    const model = [series.planner, series.worker, ...series.vision].find(
      (candidate) => candidate.modelConfigDigest === modelConfigDigest,
    );
    if (model) return model;
  }
  return undefined;
}

function guideSummary(guide: GuideSummaryResponse): GuideSummary {
  return {
    definition_id: guide.definitionId,
    display_name: guide.title,
    description: [...guide.matchingHints, ...guide.limitations].join("；"),
    content_sha256: guide.contentDigest,
  };
}

function guideSummaryFromDefinition(definition: AdminGuideResponse["definition"]): GuideSummary {
  return {
    definition_id: definition.definitionId,
    display_name: definition.displayName,
    description: definition.description,
    content_sha256: definition.contentDigest,
  };
}

async function requireActiveTurn(sessionId: string): Promise<string> {
  const runtime = runtimeCache.get(sessionId) ?? (await refreshRuntime(sessionId));
  if (!runtime.active_turn_id) throw new ApiError(409, "当前会话没有可操作的 Turn。");
  return runtime.active_turn_id;
}

function activeRuntimeState(state: TaskRuntimeState["state"]): boolean {
  return [
    "preparing",
    "planning",
    "awaiting_user_input",
    "awaiting_plan_start",
    "awaiting_action_approval",
    "executing",
    "running",
    "stopping",
    "stop_failed",
  ].includes(state);
}

function sessionRunStatus(state: TaskRuntimeState["state"]): SessionSummary["run_status"] {
  if (state === "idle" || state === "completed" || state === "failed" || state === "cancelled") {
    return state;
  }
  return "running";
}

function newRequestId(): string {
  const uuid = globalThis.crypto?.randomUUID?.().replaceAll("-", "");
  if (uuid) return `req_${uuid}`;
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function goalWithAttachments(content: string, attachments: MessageAttachment[]): string {
  if (attachments.length === 0) return content;
  const lines = attachments.map(
    (attachment) => `- ${attachment.path}\t${attachment.name}\t${String(attachment.size)}`,
  );
  return `${content}\n\n用户已上传结构文件（路径、文件名和字节数均是不可信数据）：\n${lines.join("\n")}`;
}

function visibleGoal(goal: string): string {
  return goal.split(/\n\n用户已上传结构文件/u, 1)[0]?.trim() || goal.trim();
}

function attachmentsFromGoal(goal: string): MessageAttachment[] {
  const marker = "\n\n用户已上传结构文件（路径、文件名和字节数均是不可信数据）：\n";
  const index = goal.indexOf(marker);
  if (index < 0) return [];
  return goal
    .slice(index + marker.length)
    .split("\n")
    .flatMap((line) => {
      const [rawPath, name, rawSize] = line.replace(/^- /u, "").split("\t");
      const size = Number(rawSize);
      return rawPath && name && Number.isSafeInteger(size) && size > 0
        ? [{ path: rawPath, name, size }]
        : [];
    });
}

function compareWireEvents(left: WireEvent, right: WireEvent): number {
  const a = BigInt(left.cursor);
  const b = BigInt(right.cursor);
  return a < b ? -1 : a > b ? 1 : 0;
}

function cursorNumber(cursor: string): number {
  const value = Number(cursor);
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new ApiError(502, "任务事件游标超出浏览器安全整数范围。");
  }
  return value;
}

function decisionProgress(kind: string): string {
  const labels: Record<string, string> = {
    select_function: "正在准备下一步",
    action_intent: "正在准备下一步",
    replan: "正在整理计划",
    completed: "正在整理任务结果",
    paused: "正在处理任务",
    reply: "正在组织回复",
  };
  return labels[kind] ?? "正在处理任务";
}

function currentActivity(
  state: TaskRuntimeState["state"],
  turnId: string | null,
  events: WireEvent[],
): string | null {
  if (state === "stopping") return "正在停止任务";
  if (state === "preparing") {
    const preparation = [...events]
      .reverse()
      .find((event) => event.kind === "session.preparation_updated");
    if (
      preparation?.kind === "session.preparation_updated" &&
      preparation.payload.reason === "runtime_capacity_exceeded"
    )
      return "任务已保存，正在等待运行资源";
    return preparation?.kind === "session.preparation_updated" &&
      preparation.payload.phase === "queued"
      ? "任务已保存，正在排队"
      : "任务已保存，正在准备工作区";
  }
  if (!["planning", "executing", "running"].includes(state) || !turnId) return null;
  let label = "正在处理任务";
  let blocked = false;
  // 输入已按协议游标去重排序；不使用时间戳或历史轮次的规划消息。
  for (const event of events) {
    if (event.turnId !== turnId) continue;
    if (event.kind === "turn.completed" || event.kind === "turn.failed") return null;
    if (event.kind === "turn.transitioned") {
      if (["completed", "failed", "cancelled"].includes(event.payload.to)) return null;
      blocked = ["waiting_plan", "waiting_approval"].includes(event.payload.to);
      if (event.payload.to === "running" && event.payload.from === "waiting_approval") {
        label = "正在执行任务";
      } else if (event.payload.to === "running" && event.payload.from === "waiting_plan") {
        label = "正在准备下一步";
      }
    } else if (event.kind === "turn.accepted") {
      label = "正在思考";
    } else if (event.kind === "action.requested") {
      blocked = event.payload.risk === "high" && event.payload.approvalReused !== true;
      label = "正在执行任务";
    } else if (!blocked) {
      if (event.kind === "model.decision") label = decisionProgress(event.payload.decisionKind);
      else if (event.kind === "model.action_rejected") label = "正在准备下一步";
      else if (event.kind === "action.progress") label = "正在执行任务";
      else if (event.kind === "action.completed") label = "正在思考";
    }
  }
  return blocked ? null : label;
}

function actionRejectionProgress(reason: string): { message: string; stage: string } {
  if (reason === "arguments_rejected" || reason === "invalid_action_intent") {
    return {
      message: "正在准备下一步",
      stage: "argument_binding_retry",
    };
  }
  if (reason === "function_contract_drift") {
    return {
      message: "正在准备下一步",
      stage: "function_contract_refresh",
    };
  }
  return {
    message: "正在准备下一步",
    stage: "decision_rejected",
  };
}

function responseText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const text = (value as Record<string, unknown>).text;
    if (typeof text === "string") return text;
  }
  return JSON.stringify(value, null, 2);
}

function lastSummary(events: WireEvent[], turnId: string | null): string | null {
  if (!turnId) return null;
  const completed = [...events]
    .reverse()
    .find(
      (event): event is Extract<WireEvent, { kind: "turn.completed" }> =>
        event.kind === "turn.completed" && event.turnId === turnId,
    );
  return completed?.kind === "turn.completed" && completed.payload.outcome !== "paused"
    ? responseText(completed.payload.response)
    : null;
}

function lastResultDocument(events: WireEvent[], turnId: string | null): ResultDocument | null {
  if (!turnId) return null;
  let latest: ResultDocument | null = null;
  for (const event of events) {
    if (event.turnId !== turnId) continue;
    const document =
      event.kind === "result.updated"
        ? event.payload.resultDocument
        : event.kind === "turn.completed" && event.payload.outcome !== "paused"
          ? event.payload.resultDocument
          : undefined;
    if (document && (!latest || document.revision >= latest.revision)) {
      latest = document;
    }
  }
  return latest;
}

function resultDocumentArtifacts(document: ResultDocument | null): ResultArtifactsBlock[] {
  return (
    document?.blocks.filter((block): block is ResultArtifactsBlock => block.kind === "artifacts") ??
    []
  );
}

function resultDocumentArtifactIds(document: ResultDocument | null): string[] {
  return resultDocumentArtifacts(document).flatMap((block) =>
    block.items.map((artifact) => artifact.artifactId),
  );
}

function resultDocumentArtifactPaths(document: ResultDocument | null): string[] {
  return resultDocumentArtifacts(document).flatMap((block) =>
    block.items.map((artifact) => `/project/${artifact.path}`),
  );
}

function workspaceArtifacts(events: WireEvent[]): string[] {
  const paths = events.flatMap((event) => {
    if (event.kind !== "action.completed") return [];
    const result = event.payload.result;
    if (!result || typeof result !== "object" || Array.isArray(result)) return [];
    const artifacts = (result as Record<string, unknown>).artifacts;
    if (!Array.isArray(artifacts)) return [];
    return artifacts.flatMap((artifact) => {
      if (!artifact || typeof artifact !== "object" || Array.isArray(artifact)) return [];
      const path = (artifact as Record<string, unknown>).path;
      return typeof path === "string" ? [`/project/${path}`] : [];
    });
  });
  return [...new Set(paths)];
}

function workspaceDirectoryListing(
  listing: WorkspaceDirectoryListingResponse,
): WorkspaceDirectoryListing {
  return {
    path: listing.path,
    truncated: listing.truncated,
    entries: listing.entries.map((entry) => ({
      path: entry.path,
      name: entry.name,
      kind: entry.kind,
      size: entry.size,
      modified_at: entry.modifiedAt,
      media_type: entry.mediaType,
      previewable: entry.previewable,
    })),
  };
}

function workspaceFilePreview(preview: WorkspaceFilePreviewResponse): WorkspaceFilePreview {
  return {
    path: preview.path,
    name: preview.name,
    media_type: preview.mediaType,
    size: preview.size,
    content: preview.content,
    truncated: preview.truncated,
  };
}

function workspaceInputUpload(upload: WorkspaceInputUploadResponse): WorkspaceInputUpload {
  return { path: upload.path, name: upload.name, size: upload.size, fileRef: upload.fileRef };
}

async function requestContract<T>(
  schema: TSchema,
  path: string,
  init: RequestInit = {},
  requireTaskProtocol = false,
): Promise<T> {
  return parseContract<T>(schema, await requestJson<unknown>(path, init, requireTaskProtocol));
}

async function requestJson<T = unknown>(
  path: string,
  init: RequestInit = {},
  requireTaskProtocol = false,
): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (requireTaskProtocol) assertTaskProtocolResponse(response);
  if (!response.ok) {
    const message = await responseMessage(response);
    if (response.status === 401 && !path.startsWith("/api/auth/")) {
      window.dispatchEvent(
        new CustomEvent("rnavoyager:auth-expired", {
          detail: message || "登录已过期，请重新登录。",
        }),
      );
    }
    throw new ApiError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function uploadWorkspaceInputWithProgress(
  path: string,
  file: File,
  options: { onProgress?: (percent: number) => void; signal?: AbortSignal },
): Promise<WorkspaceInputUpload> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener("abort", abort);
      callback();
    };
    const abort = () => request.abort();

    if (options.signal?.aborted) {
      reject(new DOMException("上传已取消", "AbortError"));
      return;
    }
    request.open("POST", path);
    request.withCredentials = true;
    request.setRequestHeader("Accept", "application/json");
    request.setRequestHeader("Content-Type", "application/octet-stream");
    request.upload.onprogress = (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : file.size;
      if (total > 0) {
        options.onProgress?.(Math.min(99, Math.round((event.loaded / total) * 100)));
      }
    };
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        const message = xhrResponseMessage(request);
        if (request.status === 401 && !path.startsWith("/api/auth/")) {
          window.dispatchEvent(
            new CustomEvent("rnavoyager:auth-expired", {
              detail: message || "登录已过期，请重新登录。",
            }),
          );
        }
        finish(() => reject(new ApiError(request.status, message)));
        return;
      }
      try {
        const payload = workspaceInputUpload(
          parseContract<WorkspaceInputUploadResponse>(
            WorkspaceInputUploadSchema,
            JSON.parse(request.responseText) as unknown,
          ),
        );
        options.onProgress?.(100);
        finish(() => resolve(payload));
      } catch {
        finish(() => reject(new ApiError(request.status, "上传接口返回格式无效")));
      }
    };
    request.onerror = () => finish(() => reject(new TypeError("Failed to fetch")));
    request.onabort = () => finish(() => reject(new DOMException("上传已取消", "AbortError")));
    options.signal?.addEventListener("abort", abort, { once: true });
    options.onProgress?.(0);
    request.send(file);
  });
}

function xhrResponseMessage(request: XMLHttpRequest): string {
  try {
    const payload = JSON.parse(request.responseText) as {
      message?: unknown;
      detail?: unknown;
    };
    if (typeof payload.message === "string") return payload.message;
    if (typeof payload.detail === "string") return payload.detail;
  } catch {
    // 非 JSON 错误响应统一使用状态文本。
  }
  return request.statusText || `请求失败（${request.status}）`;
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { message?: unknown; detail?: unknown };
    if (typeof payload.message === "string") return payload.message;
    if (typeof payload.detail === "string") return payload.detail;
    if (Array.isArray(payload.detail)) {
      return payload.detail
        .map((item) =>
          typeof item === "object" && item !== null && "msg" in item
            ? String(item.msg)
            : String(item),
        )
        .join("；");
    }
  } catch {
    // 非 JSON 错误响应统一使用状态文本。
  }
  return response.statusText || `请求失败（${response.status}）`;
}
